import { useEffect, useRef } from "react";
import { Bold, Italic, Underline, Heading1, List, ListOrdered } from "lucide-react";

import { Button } from "./ui/button";

interface RichTextEditorProps {
  id?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

/**
 * Minimal Bold/Italic/Underline/H1/list WYSIWYG editor, matching the mobile
 * app's "Marketing Message" editor. Uses contentEditable + execCommand rather
 * than pulling in a full editor library - this codebase already relies on
 * execCommand elsewhere (PlatformBilling's copy-link fallback) and the
 * formatting needs here are limited to what the toolbar exposes.
 */
export function RichTextEditor({ id, value, onChange, placeholder }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);

  // Only pushes `value` into the DOM when it changed from outside (e.g. the
  // dialog opened with a different coupon) - after the user's own typing,
  // onChange already set the parent's state to this exact HTML, so the
  // innerHTML check below is false and the cursor position is left alone.
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || "")) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (command: string, commandValue?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, commandValue);
    onChange(editorRef.current?.innerHTML || "");
  };

  const toolbarButton = (
    label: string,
    icon: React.ReactNode,
    onClick: () => void
  ) => (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      title={label}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {icon}
    </Button>
  );

  return (
    <div className="rounded-md border">
      <div className="flex items-center gap-0.5 border-b p-1">
        {toolbarButton("Bold", <Bold className="h-4 w-4" />, () => exec("bold"))}
        {toolbarButton("Italic", <Italic className="h-4 w-4" />, () => exec("italic"))}
        {toolbarButton("Underline", <Underline className="h-4 w-4" />, () => exec("underline"))}
        {toolbarButton("Heading", <Heading1 className="h-4 w-4" />, () =>
          exec("formatBlock", "H1")
        )}
        {toolbarButton("Bullet list", <List className="h-4 w-4" />, () =>
          exec("insertUnorderedList")
        )}
        {toolbarButton("Numbered list", <ListOrdered className="h-4 w-4" />, () =>
          exec("insertOrderedList")
        )}
      </div>
      <div
        ref={editorRef}
        id={id}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current?.innerHTML || "")}
        data-placeholder={placeholder}
        className="min-h-32 px-3 py-2 text-sm outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground"
      />
    </div>
  );
}
